import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CourseTypeClient, CourseTypeDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CourseTypeClient(API_BASE_URL, axiosInstance);

// Cached lookup for dropdowns (infinite stale time)
export const useCourseTypes = () => {
  return useQuery({
    queryKey: ['courseTypes'],
    queryFn: async () => {
      const response = await apiClient.searchCourseTypes({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search for List pages
export const useCourseTypeSearch = () => {
  const search = async (filters: any) => {
    const response = await apiClient.searchCourseTypes(filters);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Get single item by ID
export const useCourseTypeById = (id: string) => {
  return useQuery({
    queryKey: ['courseType', id],
    queryFn: async () => {
      const response = await apiClient.getCourseTypeById(id);
      return response?.data;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateCourseType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CourseTypeDTO) => {
      return await apiClient.createCourseType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courseTypes'] });
    },
  });
};

// Update mutation
export const useUpdateCourseType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CourseTypeDTO }) => {
      return await apiClient.updateCourseType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courseTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteCourseType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteCourseType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courseTypes'] });
    },
  });
};